import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import Sidebar from '../components/Sidebar';
import {
  Settings,
  AccessTime,
  WhatsApp,
  Save,
  Science, // ✅ REPLACED TestTube with Science
} from '@mui/icons-material';
import api from '../services/api';

const Settingspage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, timeSlotsRes] = await Promise.all([
        api.get('/settings'),
        api.get('/settings/time-slots')
      ]);
      setSettings(settingsRes.data);
      setTimeSlots(timeSlotsRes.data.timeSlots);
    } catch (err) {
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/settings', settings);
      setSuccess('Settings saved successfully!');
      
      // Refresh time slots
      const timeSlotsRes = await api.get('/settings/time-slots');
      setTimeSlots(timeSlotsRes.data.timeSlots);
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWhatsApp = async () => {
    try {
      await api.post('/settings/test-whatsapp', {
        phoneNumber: '+1234567890',
        message: 'Test message from admin panel'
      });
      setSuccess('WhatsApp test message sent successfully!');
    } catch (err) {
      setError('Failed to send WhatsApp test message');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
          System Settings
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
       <Grid item xs={12} style={{marginBottom:15}}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>

      <Grid container spacing={3}>
  
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
              Booking Configuration
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Slot Duration</InputLabel>
                  <Select
                    value={settings.slotDuration}
                    label="Slot Duration"
                    onChange={(e) => handleChange('slotDuration', e.target.value)}
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Opening Time"
                  type="time"
                  value={settings.openingTime}
                  onChange={(e) => handleChange('openingTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Closing Time"
                  type="time"
                  value={settings.closingTime}
                  onChange={(e) => handleChange('closingTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Advance Booking (days)"
                  type="number"
                  value={settings.maxAdvanceBooking}
                  onChange={(e) => handleChange('maxAdvanceBooking', parseInt(e.target.value))}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Min Advance Booking (hours)"
                  type="number"
                  value={settings.minAdvanceBooking}
                  onChange={(e) => handleChange('minAdvanceBooking', parseInt(e.target.value))}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoConfirmBookings}
                      onChange={(e) => handleChange('autoConfirmBookings', e.target.checked)}
                    />
                  }
                  label="Auto-confirm bookings"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* WhatsApp Settings */}
        <Grid item xs={12} md={6} style={{display:"none"}}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <WhatsApp sx={{ mr: 1, verticalAlign: 'middle' }} />
              WhatsApp Integration
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsappEnabled}
                      onChange={(e) => handleChange('whatsappEnabled', e.target.checked)}
                    />
                  }
                  label="Enable WhatsApp integration"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="WhatsApp Webhook URL"
                  value={settings.whatsappWebhookUrl}
                  onChange={(e) => handleChange('whatsappWebhookUrl', e.target.value)}
                  placeholder="https://your-domain.com/api/webhooks/whatsapp"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="WhatsApp Access Token"
                  type="password"
                  value={settings.whatsappToken}
                  onChange={(e) => handleChange('whatsappToken', e.target.value)}
                  placeholder="Your WhatsApp Business API token"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="WhatsApp Phone Number ID"
                  value={settings.whatsappPhoneNumberId}
                  onChange={(e) => handleChange('whatsappPhoneNumberId', e.target.value)}
                  placeholder="Your WhatsApp Business phone number ID"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<Science />} // ✅ changed from <TestTube />
                  onClick={handleTestWhatsApp}
                  disabled={!settings.whatsappEnabled}
                >
                  Test WhatsApp
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Time Slots Preview */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generated Time Slots
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {timeSlots.map((slot, index) => (
                <Chip
                  key={index}
                  label={slot}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Total slots: {timeSlots.length}
            </Typography>
          </Paper>
        </Grid>

        {/* Save Button */}
     
      </Grid>
      </Box>
    </Box>
  );
};

export default Settingspage;
